import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileCheck, MessageSquare, Newspaper, Loader2, ArrowRight, BadgeCheck, UserCheck } from 'lucide-react';
import api from '../../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    api.get('/admin/stats').then(({ data }) => setStats(data)).catch(() => {});
    api.get('/admin/requests?status=pending').then(({ data }) => setRequests(data.slice(0, 5))).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
      </div>
    );
  }

  const cards = [
    { label: 'Socios activos', value: stats.active_members, sub: `${stats.total_members} en total`, icon: BadgeCheck, color: 'bg-emerald-500/10 text-emerald-600' },
    { label: 'Solicitudes pendientes', value: stats.pending_requests, sub: 'Por revisar', icon: FileCheck, color: 'bg-amber-500/10 text-amber-600', link: '/admin/solicitudes' },
    { label: 'Mensajes de contacto', value: stats.contact_messages, sub: `${stats.new_messages} sin leer`, icon: MessageSquare, color: 'bg-sky-500/10 text-sky-600', link: '/admin/mensajes' },
    { label: 'Usuarios registrados', value: stats.total_users, sub: 'Cuentas en la plataforma', icon: UserCheck, color: 'bg-indigo-500/10 text-indigo-600', link: '/admin/usuarios' },
    { label: 'Publicaciones en feed', value: stats.posts, sub: 'Comunidad CNIB Connect', icon: Users, color: 'bg-orange-500/10 text-orange-600' },
    { label: 'Noticias publicadas', value: stats.news, sub: 'Portal público', icon: Newspaper, color: 'bg-purple-500/10 text-purple-600', link: '/admin/noticias' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Visión general</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Panel CNIB</h1>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-stats-grid">
        {cards.map((card) => {
          const Wrapper = card.link ? Link : 'div';
          return (
            <Wrapper
              key={card.label}
              {...(card.link ? { to: card.link } : {})}
              className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex items-center justify-between">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.color}`}>
                  <card.icon className="h-5 w-5" />
                </span>
                <span className="font-heading text-3xl font-extrabold text-slate-900">{card.value}</span>
              </div>
              <p className="mt-4 text-sm font-bold text-slate-800">{card.label}</p>
              <p className="text-xs text-slate-500">{card.sub}</p>
            </Wrapper>
          );
        })}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-900">Solicitudes recientes</h2>
          <Link to="/admin/solicitudes" className="flex items-center gap-1 text-sm font-bold text-orange-500 hover:text-orange-600">
            Gestionar <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {requests.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">No hay solicitudes pendientes. Todo al día.</p>
          )}
          {requests.map((req) => (
            <div key={req.request_id} className="flex items-center justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-bold text-slate-800">{req.full_name}</p>
                <p className="text-xs text-slate-500">
                  {req.professional_type} · {req.city}
                </p>
              </div>
              <span className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-600">Pendiente</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
